import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArFollowUpBuilder, arFollowUpById, blankArDraft, olderBalancesSteps } from '@elva/ui';
import type { ArBuilderPage, ArFollowUpDraft } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

/**
 * The follow-up builder (amendment 28 §4). Three pages over one draft, held in a
 * module-level store so the draft survives page-to-page navigation — the same
 * pattern the recall and TC builders use.
 *
 * `/collections/follow-ups/new` (optionally `?desc=` from a Balances row's
 * *Start a follow-up*) and `/collections/follow-ups/<id>/edit`.
 */
const store: { draft: ArFollowUpDraft; loadedFor: string | undefined } = {
  draft: blankArDraft(),
  loadedFor: undefined,
};

function loadFor(key: string, desc: string): ArFollowUpDraft {
  if (store.loadedFor === key) return store.draft;
  if (key === 'new') {
    store.draft = blankArDraft(desc);
  } else {
    const row = arFollowUpById(key);
    // The Older-balances follow-up demonstrates the full five-step sequence.
    const steps = key === 'older-balances' ? olderBalancesSteps() : blankArDraft().steps;
    store.draft = { id: key, name: row?.name ?? '', description: row?.description ?? '', steps };
  }
  store.loadedFor = key;
  return store.draft;
}

function useArBuilder(key: string, desc: string) {
  const [draft, setLocal] = useState<ArFollowUpDraft>(() => loadFor(key, desc));
  const update = useCallback((next: ArFollowUpDraft) => { store.draft = next; setLocal(next); }, []);
  return [draft, update] as const;
}

export function CollectionsFollowUpBuilderPage({ page }: { page: ArBuilderPage }) {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const key = id ?? 'new';
  const [draft, setDraft] = useArBuilder(key, params.get('desc') ?? '');

  if (id !== undefined && arFollowUpById(id) === undefined) return <Navigate to="/collections/follow-ups" replace />;

  const base = id === undefined ? '/collections/follow-ups/new' : `/collections/follow-ups/${id}/edit`;

  return (
    <ArFollowUpBuilder
      page={page}
      draft={draft}
      onChange={setDraft}
      onGoTo={(p) => { void navigate(`${base}${p === 'audience' ? '' : `/${p}`}`); }}
      onCancel={() => { void navigate('/collections/follow-ups'); }}
      onSaveDraft={() => { void navigate('/collections/follow-ups'); }}
      onStart={() => { void navigate('/collections/follow-ups'); }}
    />
  );
}
